import { Clip } from '../../types/clip-types';
import { CompositeSegment, ClipDeletes } from '../../types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { CompositionConfig } from '../../types/RemotionPlayerConfigTypes/CompositionConfig';
import { SegmentsTrack } from '../../types/RemotionPlayerConfigTypes/Track/SegmentsTrack';
import {
  createSegmentTransition,
  SegmentBoundaryTransition,
  SegmentTransitionType
} from '../../types/RemotionPlayerConfigTypes/Track/SegmentTransition';
import { SegmentType } from '../../types/Segment/Type';
import { TrackType } from '../../types/Track/Type';
import { MAIN_TRACK_ID } from '../../constants/player';
import { doesClipHavePlayerConfig } from '../../utils/clipUtils';

const REEL_SEGMENT_BG = '#000000';

export interface ReelSegmentRange {
  start: number;
  end: number;
}

/**
 * Turn a highlight reel's `asset_metadata.clip_segments` into ordered composite-segment
 * skeletons laid end-to-end on the reel timeline. `videos` / `layoutChanges` / `playRanges`
 * are intentionally left empty — the standard splits config generation
 * (`createCompositeFromOldComposite`) re-derives them from `segment_metadata` + the source.
 *
 * Each segment carries the same structural trim `deletes` the manual Split tool produces:
 * a head `[0, start]` and a tail `[end, sourceDuration]`. These make the removed source render
 * struck-through in the transcript, and the head delete is the "pre-trim" that cancels the
 * seek-time `startOffset` (derived from `currentBounds.fromFrame`) so timeline click-to-seek works.
 * Requires the reel clip to be `is_full_length: false` (backend guarantees this) — otherwise the
 * splits code forces `start = 0` and the window math collapses.
 */
export function buildReelCompositeSegments(clip: Clip, fps: number): CompositeSegment[] {
  const ranges: ReelSegmentRange[] = clip.asset_metadata.clip_segments ?? [];
  const contentId = clip.content?.id ?? '';
  const projectId = clip.content?.project?.id ?? '';

  // Full source-recording duration — everything outside a segment's [start,end] window is a
  // structural trim. Backend sets maxVideoDuration to the source length; fall back to the
  // furthest segment end so a tail delete never runs past the known content.
  const sourceDuration = clip.asset_metadata.maxVideoDuration ?? ranges.reduce((max, r) => Math.max(max, r.end), 0);
  const sourceDurationFrames = Math.round(sourceDuration * fps);

  let offset = 0;
  return ranges.map(({ start, end }, index) => {
    const durationSecs = Math.max(0, end - start);
    const durationFrames = Math.round(durationSecs * fps);
    const fromFrame = offset;
    const toFrame = offset + durationFrames;
    offset = toFrame;

    const sourceFrom = Math.round(start * fps);
    const sourceTo = Math.round(end * fps);

    // Structural trim deletes (source seconds). Head cancels the seek startOffset + strikes the
    // pre-window transcript; tail strikes the post-window transcript. The removed span between two
    // kept windows is double-covered by one segment's tail and the next segment's head.
    const deletes: ClipDeletes = {};
    if (start > 0) {
      deletes['0'] = { bounds: [0, start], prev_end_time: 0, next_start_time: start, deleteType: 'MANUAL' };
    }
    if (end < sourceDuration) {
      deletes[`${end}`] = {
        bounds: [end, sourceDuration],
        prev_end_time: end,
        next_start_time: sourceDuration,
        deleteType: 'MANUAL'
      };
    }

    const segment: CompositeSegment = {
      id: `${contentId}-reel-${index}`,
      name: 'Composite',
      type: SegmentType.COMPOSITE,
      fromFrame,
      toFrame,
      scenes: [],
      details: { background: { type: 'COLOR', value: REEL_SEGMENT_BG } },
      videos: [],
      layoutChanges: [],
      playRanges: [],
      projectId,
      contentId,
      // Full-source maxBounds keeps the window expandable (like a split segment). currentBounds is
      // re-derived to the [start,end] window during config-gen; maxBounds (the expand limit) is
      // preserved from here (config.ts pushes the seeded segment.maxBounds).
      maxBounds: { fromFrame: 0, toFrame: sourceDurationFrames },
      currentBounds: { fromFrame: sourceFrom, toFrame: sourceTo },
      segment_metadata: {
        start,
        end,
        duration: durationSecs,
        deletes,
        edits: {},
        merges: [],
        excludes: [],
        captions: [],
        scenes: clip.asset_metadata.scenes ?? [],
        fitMode: clip.asset_metadata.fit,
        maxVideoDuration: clip.asset_metadata.maxVideoDuration ?? sourceDuration
      }
    };
    return segment;
  });
}

/**
 * Wrap the reel's composite skeletons in a minimal `player_config` whose single SEGMENTS
 * track makes `getAllSegments` / `isSplitsInTimelineEnabledPure` treat the clip as a splits
 * clip. The real `getPlayerConfig({ shouldUpdatePlayerConfig: true })` then rebuilds
 * dimensions, captions and metadata from it.
 */
export function buildReelPlayerConfigSkeleton(clip: Clip, fps: number): CompositionConfig {
  const segments = buildReelCompositeSegments(clip, fps);
  const durationInFrames = segments.reduce((sum, s) => sum + (s.toFrame - s.fromFrame), 0);

  // One default FADE transition on each internal boundary, keyed by segment id. Transitions are
  // purely visual/audio (config.ts carries the array through untouched) — they do not change
  // durationInFrames, playRanges or the seek math.
  const segmentTransitions: SegmentBoundaryTransition[] = [];
  for (let i = 0; i < segments.length - 1; i++) {
    segmentTransitions.push(
      createSegmentTransition({
        transitionType: SegmentTransitionType.FADE,
        fromSegmentId: segments[i].id,
        toSegmentId: segments[i + 1].id,
        fps
      })
    );
  }

  const segmentsTrack: SegmentsTrack = {
    id: MAIN_TRACK_ID,
    name: 'Segments Track',
    type: TrackType.SEGMENTS,
    hideTrack: { inTimeline: false, inPlayer: false },
    segments,
    segmentTransitions
  };
  return {
    height: 0,
    width: 0,
    fps,
    durationInFrames,
    version: '0.0.1',
    playerConfig: {
      tracks: [segmentsTrack],
      metadata: {}
    }
  } as unknown as CompositionConfig;
}

/**
 * If a clip carries `clip_segments` and has no `player_config` yet, initialise its
 * `player_config` with the reel skeleton. Idempotent. Returns true when it initialised.
 */
export function initReelConfig(clip: Clip, fps: number): boolean {
  const hasSegments = (clip.asset_metadata.clip_segments?.length ?? 0) > 0;
  if (!hasSegments) {
    return false;
  }
  if (doesClipHavePlayerConfig(clip)) {
    return false;
  }
  clip.asset_metadata.player_config = buildReelPlayerConfigSkeleton(clip, fps);
  return true;
}
